import { shallowRef, ref } from "vue";
import { defineStore } from "pinia";

// useRuntimeConfig 类型已在全局 index.d.ts 中声明
// Nuxt 会自动导入此 composable

export interface Menu {
  id?: string;
  name: string;
  path?: string;
  children?: Menu[];
  desc?: string;
  disabled?: boolean;
  [key: string]: any;
}

export interface ContentConfig {
  title: string;
  subTitle: string;
  describe: string;
  backgroundImage: string;
  productImage: string;
  isFullScreen?: boolean;
}

// 创建菜单配置的函数，支持动态 URL
function createMenuConfig(hmzlUrl: string): Menu[] {
  return [
    { name: "首页", path: "/" },
    { name: "和码智联", path: hmzlUrl },
    {
      id: "product",
      name: "产品介绍",
      children: [
        {
          id: "upstream",
          name: "上游生产端",
          desc: "大型制造企业",
          children: [
            {
              id: "brand-supply",
              name: "品牌供应链系统",
              path: "/products/supply-chain/",
              disabled: false,
              content: {},
            },
            {
              id: "hgys",
              name: "和贵云商",
              path: "/products/hgys/",
              disabled: false,
            },
          ],
        },
        {
          id: "midstream",
          name: "中游渠道端",
          desc: "企业到经销商",
          children: [
            {
              id: "feimao",
              name: "飞马外勤",
              path: "/products/feima/",
              disabled: false,
            },
            {
              id: "dms",
              name: "DMS经销商渠道管理平台",
              path: "/products/dms/",
              disabled: false,
            },
            {
              id: "one-code",
              name: "一物一码",
              path: "/products/one-code/",
              disabled: false,
            },
          ],
        },
        {
          id: "downstream",
          name: "下游终端",
          desc: "经销商到门店",
          children: [
            {
              id: "kww-member",
              name: "和掌贵",
              path: "/products/hzg/",
              disabled: false,
            },
            {
              id: "merchant-mall",
              name: "和贵惠供",
              path: "/products/hghg/",
              disabled: false,
            },
          ],
        },
        {
          id: "consumer",
          name: "消费端",
          desc: "基于下游终端的线上延伸",
          children: [
            {
              id: "kouwei-online",
              name: "线上商城",
              path: "/products/kww-online/",
              disabled: false,
            },
            {
              id: "redemption-center",
              name: "会员兑换中心",
              path: "/products/redemption-center/",
              disabled: false,
            },
            {
              id: "chengtian-member",
              name: "会员中心",
              path: "/products/hctx-customer-center/",
              disabled: false,
            },
          ],
        },
        {
          id: "offline-retail",
          name: "线下新零售",
          desc: "多场景业务拓展",
          children: [
            {
              id: "kuaileda",
              name: "快乐达",
              path: "/products/kld/",
              disabled: false,
            },
            {
              id: "jishi",
              name: "即时鸟",
              path: "/products/jsn/",
              disabled: false,
            },
            {
              id: "jishiang",
              name: "即时榔",
              path: "/products/jsl/",
              disabled: false,
            },
            {
              id: "jdd",
              name: "即得电",
              path: "/products/jdd/",
              disabled: false,
            },
            {
              id: "store-cashier",
              name: "门店收银系统",
              path: "/products/store-cashier/",
              disabled: false,
            },
          ],
        },
        {
          id: "business-support",
          name: "业务支撑",
          desc: " ",
          children: [
            {
              id: "settlement",
              name: "结算中心",
              path: "/products/settlement/",
              disabled: false,
            },
          ],
        },
      ],
    },
    { name: "关于我们", path: "/about-us/" },
  ];
}

// Mock API 客户端
const MockApiClient = {
  getFaqs: async (query: Partial<any>) => {
    return [];
  },
};

export const useMenuStore = defineStore("menu", () => {
  // 在 store 内部正确调用 useRuntimeConfig
  const config = useRuntimeConfig();

  // 动态初始化菜单数据
  const menu = shallowRef<Menu[]>(createMenuConfig(config.public.hmzlUrl));

  // 保存当前激活的路由信息
  const currentRoute = ref<{
    path?: string;
    menuItem?: Menu;
    timestamp?: number;
  } | null>(null);

  async function fetchMenu(query: Partial<any>) {
    return MockApiClient.getFaqs(query);
  }

  // 保存路由对象
  function saveRoute(menuItem: Menu, path: string) {
    currentRoute.value = {
      path,
      menuItem,
      timestamp: Date.now(),
    };

    if (import.meta.client) {
      localStorage.setItem("lastRoute", JSON.stringify(currentRoute.value));
    }
  }

  // 从 localStorage 恢复路由
  function restoreRoute() {
    if (import.meta.client) {
      const saved = localStorage.getItem("lastRoute");
      if (saved) {
        try {
          currentRoute.value = JSON.parse(saved);
        } catch (e) {
          console.error("Failed to restore route:", e);
        }
      }
    }
  }

  // 清除保存的路由
  function clearRoute() {
    currentRoute.value = null;
    if (import.meta.client) {
      localStorage.removeItem("lastRoute");
    }
  }

  /**
   * Finds a menu item by its ID in a nested menu array.
   */
  function findMenuById(id: string, menus: Menu[] = menu.value): Menu | null {
    for (const item of menus) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const foundInChildren = findMenuById(id, item.children);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  }

  return {
    fetchMenu,
    menu,
    currentRoute,
    saveRoute,
    restoreRoute,
    clearRoute,
    findMenuById,
  };
});
